/**
 * Settings-feature API layer + i18n namespace registration (design-revamp,
 * handoff-2026-06 — `screens-misc.jsx` Settings + `copy.jsx` `settings`).
 *
 * The `settings` i18n namespace (ru/ky/en, all first-class per ADR-028) is
 * registered here via `i18n.addResourceBundle` rather than in
 * `apps/web/src/lib/i18n.ts` (out of this feature's allowed-touch list),
 * mirroring `features/account/api.ts` + `features/chat/chat-store.ts`. The
 * bundles load once at module import; `useTranslation("settings")` then
 * resolves keys with the documented RU-fallback chain (REQ-PRD-§13.2-A).
 *
 * Data sources are reused, not recreated:
 *   - Profile → `getMe()` from `features/account/api`.
 *   - Delete  → `deleteAccount()` + `DeleteAccountDialog` from the account
 *               feature.
 *   - Export  → `GET /me/export` (ZIP) via an authenticated `fetch` (the
 *               access token lives in JS memory, so a plain `<a download>`
 *               can't carry the Bearer header — same shape as
 *               `PrivacyScreen.onExport`).
 *   - Logout  → `POST /auth/logout` via `apiClient` (mutating → carries an
 *               Idempotency-Key) + `auth-store.clearAuth()` +
 *               `endLocalSession()` (W0.3).
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import i18n from "@/lib/i18n";
import { apiClient, AUTH_PATHS } from "@/lib/api-client";
import { getMe, type MeProfile } from "@/features/account/api";
import { endLocalSession } from "@/features/chat/session-end";
import { useAuthStore } from "@/lib/auth-store";
import { queryClient } from "@/lib/queryClient";
import type { Palette } from "@/lib/ui-store";

import settingsEn from "@/locales/en/settings.json";
import settingsKy from "@/locales/ky/settings.json";
import settingsRu from "@/locales/ru/settings.json";

const SETTINGS_NS = "settings";

let settingsI18nRegistered = false;

/**
 * Register the `settings` i18n namespace (ru/ky/en) on the shared i18next
 * singleton. Idempotent + init-safe: `addResourceBundle` is only callable
 * once the instance is initialized (production `initI18n()` runs in
 * `main.tsx` at boot; tests init in `beforeEach`), so we guard on
 * `i18n.isInitialized` and the method's presence and remember we've run.
 * Called at the top of `SettingsScreen` render so the keys are present
 * regardless of import order. 4th arg `deep=true` merges nested objects;
 * 5th `overwrite=false` keeps an existing bundle (HMR-safe).
 */
export function registerSettingsI18n(): void {
  if (settingsI18nRegistered) return;
  if (!i18n.isInitialized || typeof i18n.addResourceBundle !== "function") {
    return;
  }
  i18n.addResourceBundle("ru", SETTINGS_NS, settingsRu, true, false);
  i18n.addResourceBundle("ky", SETTINGS_NS, settingsKy, true, false);
  i18n.addResourceBundle("en", SETTINGS_NS, settingsEn, true, false);
  settingsI18nRegistered = true;
}

// Best-effort eager registration for the production path (initI18n() runs
// before any feature module imports in main.tsx). In tests where init
// happens in beforeEach, SettingsScreen calls registerSettingsI18n() on render.
registerSettingsI18n();

// ─────────────────────────── profile query ───────────────────────────

/** Stable TanStack Query key for the signed-in user's profile (`GET /me`). */
export const settingsQueryKeys = {
  me: ["me"] as const,
} as const;

/**
 * The signed-in user's profile (header card). Reuses `getMe()` from the
 * account feature; cached under `["me"]` so it is shared if other surfaces
 * read the same profile. PII (email/phone) is server-state only — it lives
 * in TanStack Query, never in localStorage.
 *
 * `enabled` (SCRUM-145): route guards read this BEFORE they know whether a
 * token exists. Firing it unauthenticated 401s, and the api-client's
 * persistent-401 handler then hard-navigates the tab — beating the guard's own
 * <Navigate/>. Pass `{ enabled: isAuthenticated }` from a guard. Defaults to
 * `true` so every existing caller is unchanged.
 */
export function useMe(
  options?: { enabled?: boolean },
): UseQueryResult<MeProfile, Error> {
  return useQuery<MeProfile, Error>({
    queryKey: settingsQueryKeys.me,
    queryFn: getMe,
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * The four brand palettes in their canonical display order (mirrors
 * `GARDEN_PALETTES` in `system.jsx` / the `Palette` union in `ui-store`).
 * The swatch chips are rendered token-driven (each swatch button carries
 * `data-palette={value}`, so `theme.css` resolves `--g-primary`/`--g-accent`
 * for that palette via the CSS cascade — no hardcoded hex in the JSX).
 */
export const PALETTE_OPTIONS: readonly Palette[] = [
  "teal",
  "sage",
  "terracotta",
  "indigo",
];

/** Empty JSON body for the mutating logout endpoint (takes no payload). */
type EmptyBody = Record<string, never>;

/**
 * Log out: revoke the refresh-cookie session server-side, then clear the
 * in-memory access token. Best-effort — a network/4xx failure on the POST
 * still clears local auth so the user lands signed-out (the caller
 * navigates home). `apiClient.post` auto-attaches the Idempotency-Key.
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post<void, EmptyBody>(AUTH_PATHS.logout, {});
  } catch {
    // Swallow — the refresh cookie may already be invalid; clearing the
    // in-memory token below is what actually signs this tab out.
  } finally {
    useAuthStore.getState().clearAuth();
    // Drop every cached query so the next user who signs in (without a full
    // page reload) never sees the previous user's profile/threads/etc. Without
    // this, a SPA navigate() after logout leaves stale TanStack Query data in
    // memory until a hard refresh recreates the client (SCRUM-138).
    queryClient.clear();
    // W0.3: the chat store too — otherwise the previous person's open thread
    // survives in this tab (shared devices).
    endLocalSession();
  }
}

/**
 * "Download my data" — re-exported from the account feature, which owns
 * `ME_PATHS` and therefore owns the export call. This module used to carry a
 * SECOND copy of the same fetch-blob-and-download routine (a third lived inline
 * in `PrivacyScreen`); the copies had already drifted. Settings keeps the name
 * so its call sites are unchanged.
 */
export { downloadMyData, type ExportOutcome } from "@/features/account/api";
